import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, throwError } from 'rxjs';

import { environment } from '../../../environments/environment';
import { APP_PLATFORM } from '../constants/app.constants';
import { AuthService } from '../services/auth.service';

/**
 * Rutas que crean o renuevan la sesión: no se les intenta refrescar el token
 * (sería recursivo y gastaría el rate limit de `/auth`).
 */
const AUTH_ENDPOINTS = ['/auth/login', '/auth/refresh', '/auth/logout', '/auth/register'];

function isAuthEndpoint(url: string): boolean {
  return AUTH_ENDPOINTS.some((path) => url.includes(path));
}

function withToken<T>(req: HttpRequest<T>, token: string): HttpRequest<T> {
  return req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
}

/**
 * El Bearer SOLO viaja a nuestra propia API.
 *
 * Antes se adjuntaba a toda peticion saliente. Hoy el panel solo llama a su
 * backend, asi que no filtraba nada — pero en cuanto alguien anadiera una
 * llamada a un tercero (analitica, mapas, un avatar remoto), el token de
 * admin se habria ido con ella en la cabecera.
 *
 * Las rutas relativas se consideran propias: Angular las resuelve contra el
 * mismo origen desde el que se sirve el panel.
 */
function isOwnApi(url: string): boolean {
  if (!/^https?:\/\//i.test(url)) return true;
  try {
    return new URL(url).origin === new URL(environment.apiUrl).origin;
  } catch {
    return false;
  }
}

/**
 * Adjunta el JWT a cada petición y, si el backend responde 401, lo renueva una
 * vez con el refresh token y reintenta.
 *
 * `AuthService.refreshSession()` es single-flight, así que varias peticiones
 * que fallen a la vez comparten un único refresh: el backend revoca todas las
 * sesiones si detecta que un refresh token ya rotado se reutiliza.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);
  const token = auth.getToken();

  // Cada app tiene sus propias cuentas: el backend usa la de este panel (y
  // rechaza con 401 un token de la cuenta de otra app).
  const ownApi = isOwnApi(req.url);
  const scoped = ownApi ? req.clone({ setHeaders: { 'X-App-Platform': APP_PLATFORM } }) : req;
  const authorized = token && ownApi ? withToken(scoped, token) : scoped;

  return next(authorized).pipe(
    catchError((error: unknown) => {
      const is401 = error instanceof HttpErrorResponse && error.status === 401;
      if (!is401 || !token || !isOwnApi(req.url) || isAuthEndpoint(req.url)) {
        return throwError(() => error);
      }

      // Un solo reintento: si con el token renovado sigue dando 401, la sesión
      // murió y el error sube al errorInterceptor, que manda a login.
      return auth.refreshSession().pipe(
        switchMap((fresh) => next(withToken(scoped, fresh))),
        catchError(() => throwError(() => error)),
      );
    }),
  );
};
